import { Controller } from "@hotwired/stimulus"


// Connects to data-controller="map"
export default class extends Controller {
  static targets = ['map', 'infoTemplate', 'pinList', 'inlineFlash', 'newPinButton']
  static values = {
    device: Object,
    pins: Array,
    baseUrl: String,
  };
  pinMarkers = [];
  activePin = null;
  map = null;
  pinMarkerHash = {};
  infoWindow = null
  moving = false
  moveClickListener = null
  csrfTokenCached = null

  connect() {
    this.csrfTokenCached = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    
    this.renderMap();

    this.renderDeviceMarker();

    if (this.hasInfoTemplateTarget) {
      this.infoWindow = new google.maps.InfoWindow()
    }
    this.renderPinList()
    this.renderPins()
  }

  renderMap() {
    let latitude = parseFloat(this.deviceValue.latitude) || 52.2493382; //noordwijkerhout
    let longitude = parseFloat(this.deviceValue.longitude) || 4.4933081; //noordwijkerhout
    this.map = new google.maps.Map(this.mapTarget, {
      center: { lat: latitude, lng: longitude },
      zoom: 13,
      mapId: 'DEMO_MAP_ID'
    })
  }

  renderDeviceMarker() {
    const position = new google.maps.LatLng(this.deviceValue.latitude, this.deviceValue.longitude);
    const markerImage = document.createElement('img');
    markerImage.src = this.deviceValue.icon;
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: position,
      map: this.map,
      title: this.deviceValue.name,
      content: markerImage
    });
  }

  newPin() {
    // max 10 pins
    if (this.pinsValue.length >= 10) {
      return;
    }

    // if there is a pin with id null, return
    if (this.pinsValue.find(p => p.id === null)) {
      return;
    }

    const currentCenter = this.map.getCenter();
    const lat = currentCenter.lat();
    const lng = currentCenter.lng();
    const newPin = {
      id: null,
      description: `beacon ${this.pinsValue.length + 1}`,
      mac_address: ``,
      latitude: lat,
      longitude: lng,
    }

    this.pinsValue = [...this.pinsValue, newPin];
    
    this.renderPinList();
    this.renderPins();

    this.focusPin(newPin);
  }

  updatePin(pin) {
    this.pinsValue = this.pinsValue.map(p => p.id === pin.id ? pin : p);
    this.renderPinList();
  }

  renderPinList() {
    this.pinListTarget.innerHTML = ""
    this.pinsValue.forEach((pin) => {
      const isActive = this.activePin?.id === pin.id
      const id = pin.id
      const li = document.createElement("li")
      let primaryLabel = id;
      let secondaryLabel = "";
      primaryLabel = pin["description"]
      secondaryLabel = pin["mac_address"]
      li.className = "pin-list-item " + (isActive ? "active" : "")
      li.dataset.pinId = id
      li.innerHTML = `
        <div>
          <div><strong>${primaryLabel}</strong></div>
          <div class="pin-list-sub">${secondaryLabel}</div>
        </div>
        <div class="pin-list-sub">${Number(pin.latitude).toFixed(5)}, ${Number(pin.longitude).toFixed(5)}</div>
      `
      li.addEventListener("click", () => this.focusPin(pin))
      this.pinListTarget.appendChild(li)
    })
  }

  renderPins() {
    this.pinMarkers.forEach((marker) => {
      marker.setMap(null)
    })
    this.pinMarkers = []
    
    this.pinsValue.forEach((pin) => {
      let latitude = null
      let longitude = null
      if (pin.latitude === null || pin.longitude === null) {
        latitude = this.map.getCenter().lat()
        longitude = this.map.getCenter().lng()
      } else {
        latitude = pin.latitude
        longitude = pin.longitude
      }
      const pinMarker = new google.maps.marker.AdvancedMarkerElement({
        position: new google.maps.LatLng(latitude, longitude),
        map: this.map,
        title: pin.description,
        gmpDraggable: true,
        id: pin.id,
      });
  
      pinMarker.addListener("dragend", () => {
        let draggedPin = this.pinsValue.find(p => p.id === pin.id)
        const {lat, lng} = pinMarker.position;
        draggedPin.latitude = lat;
        draggedPin.longitude = lng;
        this.updatePin(draggedPin);
      })
  
      pinMarker.addListener("click", () => {
        this.focusPin(pin);
      })
      this.pinMarkers.push(pinMarker);
    })
    this.renderPinList();
  }

  focusPin(pin) {
    this.activePin = pin
    const pinIndex = this.pinsValue.findIndex(p => p.id === pin.id)
    const marker = this.pinMarkers[pinIndex]
    if (!marker) return
    if (this.hasInfoTemplateTarget) {
      this.openEditor(marker, pin)
    }
    this.newPinButtonTarget.style.display = "none"
  }

  unfocusPin() {
    this.activePin = null
    this.renderPinList();
    this.renderPins();
    this.closeWindowView();
    this.newPinButtonTarget.style.display = "block"
  }

  openEditor(marker, pin) {
    pin = this.pinsValue.find(p => p.id === pin.id)
    this.closeWindowView();
    const pos = new google.maps.LatLng(pin.latitude, pin.longitude);
    this.map.panTo(pos)
    // this.map.setZoom(Math.max(this.map.getZoom(), 14))
    const fragment = this.infoTemplateTarget.content.cloneNode(true)
    const wrapper = document.createElement("div")
    wrapper.classList.add("gm-pin-iw")
    wrapper.appendChild(fragment)
    const moveBtn = wrapper.querySelector('[data-role="move"]')
    const deleteBtn = wrapper.querySelector('[data-role="delete"]')
    const saveBtn = wrapper.querySelector('[data-role="save"]')
    wrapper.querySelectorAll('[data-role="field"]').forEach((input) => {
      
      const attr = input.dataset.attr
      input.value = pin[attr] ?? ""
      input.addEventListener("input", (e) => {
        pin = this.pinsValue.find(p => p.id === pin.id)
        if (input.dataset.attr === "mac_address") {
          e.target.value = this.maskMac(e.target.value);
        } 
        pin[attr] = e.target.value
        this.updatePin(pin);
      })
    })
    saveBtn?.addEventListener("click", () => { this.savePinFromInfo(pin) })

    deleteBtn?.addEventListener("click", async () => {
      if (!confirm("Are you sure you want to delete the object?")) return
      await this.deleteObject(pin.id)
    })
    this.infoWindow.setOptions({ maxWidth: 320, disableAutoPan: false })
    this.infoWindow.setContent(wrapper)
    this.infoWindow.open({ map: this.map, anchor: marker })
  }

  maskMac(mac) {
    var lastChar = "";
    if (mac.length > 1) {
      lastChar = mac.substring(mac.length - 1)
    }

    if (!mac) return '';

    var unmasked_mac = this.unmaskMac(mac);

    if (!unmasked_mac) return '';

    var new_mac = unmasked_mac.toString( 16 )  // "4a8926c44578"
        .match( /.{1,2}/g )       // ["4a", "89", "26", "c4", "45", "78"]
        .join( ':' )              // "78:45:c4:26:89:4a"

    if (lastChar === ":" && mac.length % 3 == 0) {
      new_mac += ":";
    }
    return new_mac.toUpperCase();
  }

  unmaskMac(str) {
    return String(str).replace(/[^a-fA-F0-9]/g, '')
  }

  isMacValid(mac) {
    return this.maskMac(mac) === mac && mac.length === 17
  }


  async savePinFromInfo(pin) {
    if (!pin) return
    const createMode = !pin.id

    if ((!this.hasBaseUrlValue && !createMode) || (!this.hasBaseUrlValue && createMode)) {
      this.infoWindow?.close()
      this.showInlineFlash("Update failed because no base url is set", false)
      return
    }

    pin = this.pinsValue.find(p => p.id === pin.id) // reload the pin from the state


    if (!this.isMacValid(pin.mac_address)) {
      this.showInlineFlash("Invalid MAC address", false)
      return
    }

    const url = createMode ? this.baseUrlValue : this.baseUrlValue + "/" + pin.id
    const payload = { device_beacon: pin }

    try {
      const token = this.csrfToken()
      if (!token) {
        console.error("Missing CSRF token")
        this.showInlineFlash("Session error. Refresh page.", "danger")
        return
      }
      const res = await fetch(url, {
        method: createMode ? "POST" : "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify(payload)
      })

      if (!res.ok) throw new Error(`Update failed: ${res.status}`)
      const data = await res.json()
      const i = this.pinsValue.findIndex(p => p.id === data.id)
      if (i !== -1) {
        this.pinsValue = this.pinsValue.map(p =>
          String(p.id) === String(data.id) ? data : p
        )
      } else {
        this.pinsValue = [...this.pinsValue, data]
      }

      this.pinsValue = this.pinsValue.filter(p => p.id !== null)
      this.renderPins()

      this.showInlineFlash("Saved succesfully!", "success")
      this.unfocusPin()
    } catch (e) {
      console.error(e)
      this.showInlineFlash("Save failed", false)
    }
  }

  closeWindowView() {
    this.infoWindow?.close()
    this.renderPinList();
  }

  async deleteObject(pinId) {
    this.closeWindowView();
    if (pinId === null) {
      this.pinsValue = this.pinsValue.filter(p => p.id !== null)
      this.renderPins()
      this.showInlineFlash("Canceled beacon!", "success")
      this.unfocusPin()
      return
    }
    if (this.hasBaseUrlValue) {
      const url = this.baseUrlValue + "/" + pinId
      try {
        const token = this.csrfToken()
        if (!token) {
          console.error("Missing CSRF token")
          this.showInlineFlash("Session error. Refresh page.", "danger")
          return
        }
        const res = await fetch(url, {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": token,
            "Accept": "application/json"
          },
          credentials: "same-origin"
        })

        if (!res.ok && res.status !== 204) {
          throw new Error(`DELETE failed: ${res.status}`)
        }
        this.showInlineFlash("Removed succesfully!", "success")
        this.pinsValue = this.pinsValue.filter(p => p.id !== pinId)
        this.unfocusPin()
      } catch (e) {
        console.error(e)
        this.showInlineFlash("Could not delete object.", false)
      }
    }
  }

  csrfToken() {
    return this.csrfTokenCached
  }

  showInlineFlash(message, type = "success") {
    if (!this.hasInlineFlashTarget) return

    const el = this.inlineFlashTarget
    el.textContent = message

    el.classList.remove("text-success", "text-danger")
    el.classList.add(type === "success" ? "text-success" : "text-danger")

    el.style.display = "inline"

    clearTimeout(this.flashTimer)
    this.flashTimer = setTimeout(() => {
      el.style.display = "none"
    }, 2500)
  }

}